import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Link2,
  Check,
} from 'lucide-react';
import { Button, Badge, Table, Modal, Input, Select } from '@/components/ui';
import { JourneyTimeline } from '@/components/features/JourneyTimeline';
import { campaignService } from '@/services/campaignService';
import { procedureTypeService } from '@/services/procedureTypeService';
import type {
  CampaignStatus,
  Package,
  PackageItem,
  PatientJourney,
  JourneyStatus,
  CreatePackageRequest,
  UpdatePackageRequest,
  AddPackageItemRequest,
} from '@/types/campaign';
import styles from './CampaignDetailPage.module.css';

type ActiveTab = 'packages' | 'enrollments';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'info' | 'error';

const STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  FINISHED: 'Finalizada',
};

const STATUS_BADGE_VARIANT: Record<CampaignStatus, BadgeVariant> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  FINISHED: 'info',
};

const JOURNEY_STATUS_LABELS: Record<JourneyStatus, string> = {
  NOT_STARTED: 'Inscrito',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const JOURNEY_STATUS_BADGE_VARIANT: Record<JourneyStatus, BadgeVariant> = {
  NOT_STARTED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

/**
 * Campaign detail page showing campaign info, packages, and enrollments.
 *
 * Features:
 * - Campaign info header with status badge
 * - Tabbed interface: Pacotes | Inscricoes
 * - Pacotes tab: package cards with items, add/edit package, add items, reorder
 * - Inscricoes tab: enrolled patients with journey status
 * - JourneyTimeline preview for each package
 *
 * Fully accessible with semantic HTML, ARIA tabs, and keyboard navigation.
 */
export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const campaignId = id ?? '';

  const [activeTab, setActiveTab] = useState<ActiveTab>('packages');

  // Package modal state
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [pkgName, setPkgName] = useState('');
  const [pkgDescription, setPkgDescription] = useState('');
  const [pkgPrice, setPkgPrice] = useState('');
  const [pkgError, setPkgError] = useState<string | null>(null);

  // Add item modal state
  const [addItemToPackageId, setAddItemToPackageId] = useState<string | null>(null);
  const [itemProcedureTypeId, setItemProcedureTypeId] = useState('');
  const [itemQuantity, setItemQuantity] = useState('1');
  const [itemNotes, setItemNotes] = useState('');
  const [itemError, setItemError] = useState<string | null>(null);

  // Copy widget link
  const [linkCopied, setLinkCopied] = useState(false);

  // ── Queries ────────────────────────────────────────────

  const {
    data: campaign,
    isLoading: isCampaignLoading,
    error: campaignError,
  } = useQuery({
    queryKey: ['campaigns', campaignId],
    queryFn: () => campaignService.getCampaign(campaignId),
    enabled: Boolean(campaignId),
  });

  const { data: journeys = [], isLoading: isJourneysLoading } = useQuery({
    queryKey: ['campaigns', campaignId, 'journeys'],
    queryFn: ({ signal }) => campaignService.getJourneys(campaignId, { signal }),
    enabled: Boolean(campaignId) && activeTab === 'enrollments',
  });

  const { data: procedureTypes = [] } = useQuery({
    queryKey: ['procedure-types'],
    queryFn: () => procedureTypeService.getProcedureTypes(),
    enabled: addItemToPackageId !== null,
  });

  const handleCopyWidgetLink = useCallback(() => {
    if (!campaign) return;
    const url = `${window.location.origin}/widget/campaign/${campaign.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }, [campaign]);

  // ── Package Mutations ──────────────────────────────────

  const createPackageMutation = useMutation({
    mutationFn: (data: CreatePackageRequest) =>
      campaignService.createPackage(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      closePackageModal();
    },
    onError: () => {
      setPkgError('Erro ao criar pacote. Tente novamente.');
    },
  });

  const updatePackageMutation = useMutation({
    mutationFn: ({ packageId, data }: { packageId: string; data: UpdatePackageRequest }) =>
      campaignService.updatePackage(campaignId, packageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      closePackageModal();
    },
    onError: () => {
      setPkgError('Erro ao atualizar pacote. Tente novamente.');
    },
  });

  const deletePackageMutation = useMutation({
    mutationFn: (packageId: string) =>
      campaignService.deletePackage(campaignId, packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
    },
  });

  // ── Item Mutations ─────────────────────────────────────

  const addItemMutation = useMutation({
    mutationFn: ({ packageId, data }: { packageId: string; data: AddPackageItemRequest }) =>
      campaignService.addPackageItem(campaignId, packageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      closeAddItemModal();
    },
    onError: () => {
      setItemError('Erro ao adicionar item. Tente novamente.');
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ packageId, itemId }: { packageId: string; itemId: string }) =>
      campaignService.removePackageItem(campaignId, packageId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
    },
  });

  const reorderItemsMutation = useMutation({
    mutationFn: ({ packageId, itemIds }: { packageId: string; itemIds: string[] }) =>
      campaignService.reorderPackageItems(campaignId, packageId, { itemIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
    },
  });

  // ── Package modal handlers ─────────────────────────────

  const resetPackageForm = useCallback(() => {
    setPkgName('');
    setPkgDescription('');
    setPkgPrice('');
    setPkgError(null);
  }, []);

  const openCreatePackageModal = useCallback(() => {
    resetPackageForm();
    setEditingPackage(null);
    setIsPackageModalOpen(true);
  }, [resetPackageForm]);

  const openEditPackageModal = useCallback((pkg: Package) => {
    setPkgName(pkg.name);
    setPkgDescription(pkg.description ?? '');
    setPkgPrice(String(pkg.price));
    setPkgError(null);
    setEditingPackage(pkg);
    setIsPackageModalOpen(true);
  }, []);

  const closePackageModal = useCallback(() => {
    setIsPackageModalOpen(false);
    setEditingPackage(null);
    setPkgError(null);
  }, []);

  const handlePackageSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!pkgName.trim()) {
        setPkgError('Nome é obrigatório.');
        return;
      }
      const price = parseFloat(pkgPrice);
      if (isNaN(price) || price < 0) {
        setPkgError('Preço deve ser um valor válido.');
        return;
      }
      const payload: CreatePackageRequest = {
        name: pkgName.trim(),
        description: pkgDescription.trim() || null,
        price,
      };
      if (editingPackage) {
        updatePackageMutation.mutate({
          packageId: editingPackage.id,
          data: payload,
        });
      } else {
        createPackageMutation.mutate(payload);
      }
    },
    [pkgName, pkgDescription, pkgPrice, editingPackage, createPackageMutation, updatePackageMutation],
  );

  // ── Add item modal handlers ────────────────────────────

  const openAddItemModal = useCallback((packageId: string) => {
    setAddItemToPackageId(packageId);
    setItemProcedureTypeId('');
    setItemQuantity('1');
    setItemNotes('');
    setItemError(null);
  }, []);

  const closeAddItemModal = useCallback(() => {
    setAddItemToPackageId(null);
    setItemError(null);
  }, []);

  const handleAddItem = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!addItemToPackageId) return;
      if (!itemProcedureTypeId) {
        setItemError('Selecione um procedimento.');
        return;
      }
      const qty = parseInt(itemQuantity, 10);
      if (isNaN(qty) || qty <= 0) {
        setItemError('Quantidade deve ser um número positivo.');
        return;
      }

      // Determine the next order index from existing items
      const targetPackage = campaign?.packages.find((p) => p.id === addItemToPackageId);
      const nextOrder = targetPackage
        ? Math.max(-1, ...targetPackage.items.map((i) => i.orderIndex)) + 1
        : 0;

      const payload: AddPackageItemRequest = {
        procedureTypeId: itemProcedureTypeId,
        quantity: qty,
        orderIndex: nextOrder,
        notes: itemNotes.trim() || null,
      };
      addItemMutation.mutate({ packageId: addItemToPackageId, data: payload });
    },
    [addItemToPackageId, itemProcedureTypeId, itemQuantity, itemNotes, campaign, addItemMutation],
  );

  // ── Item reorder handlers ──────────────────────────────

  const handleMoveItem = useCallback(
    (pkg: Package, item: PackageItem, direction: 'up' | 'down') => {
      const sorted = [...pkg.items].sort((a, b) => a.orderIndex - b.orderIndex);
      const currentIndex = sorted.findIndex((i) => i.id === item.id);
      if (currentIndex === -1) return;

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return;

      // Swap the items
      const newSorted = [...sorted];
      const temp = newSorted[currentIndex];
      newSorted[currentIndex] = newSorted[targetIndex];
      newSorted[targetIndex] = temp;

      const itemIds = newSorted.map((i) => i.id);
      reorderItemsMutation.mutate({ packageId: pkg.id, itemIds });
    },
    [reorderItemsMutation],
  );

  // ── Helpers ────────────────────────────────────────────

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatEnrollDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  // ── Enrollment columns ─────────────────────────────────

  const enrollmentColumns = [
    {
      key: 'patientName',
      header: 'Paciente',
      render: (journey: PatientJourney) => (
        <span className={styles.journeyName}>{journey.patientName}</span>
      ),
    },
    {
      key: 'packageName',
      header: 'Pacote',
      width: '180px',
      render: (journey: PatientJourney) => {
        const pkg = campaign?.packages.find((p) => p.id === journey.packageId);
        return <span className={styles.journeyInfo}>{pkg?.name ?? '-'}</span>;
      },
    },
    {
      key: 'enrolledAt',
      header: 'Inscrito em',
      width: '140px',
      render: (journey: PatientJourney) => (
        <span className={styles.journeyInfo}>
          {formatEnrollDate(journey.enrolledAt)}
        </span>
      ),
    },
    {
      key: 'progress',
      header: 'Progresso',
      width: '120px',
      render: (journey: PatientJourney) => {
        const completed = journey.steps.filter(
          (s) => s.status === 'COMPLETED',
        ).length;
        const total = journey.steps.length;
        return (
          <span className={styles.journeyInfo}>
            {completed}/{total}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      render: (journey: PatientJourney) => (
        <Badge variant={JOURNEY_STATUS_BADGE_VARIANT[journey.status]}>
          {JOURNEY_STATUS_LABELS[journey.status]}
        </Badge>
      ),
    },
  ];

  // ── Available procedure types for item selection ───────

  const activeProcedureTypes = procedureTypes.filter((p) => p.isActive);
  const procedureTypeOptions = activeProcedureTypes.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.durationMinutes}min)`,
  }));

  // ── Render ─────────────────────────────────────────────

  if (isCampaignLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando campanha...</p>
        </div>
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar campanha. Verifique se a campanha existe.</p>
          <Button variant="outline" onClick={() => navigate('/admin/campaigns')}>
            Voltar para Campanhas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Back navigation */}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft size={16} />}
        onClick={() => navigate('/admin/campaigns')}
        className={styles.backButton}
      >
        Voltar
      </Button>

      {/* Campaign info header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{campaign.name}</h1>
          <div className={styles.meta}>
            <Badge variant={STATUS_BADGE_VARIANT[campaign.status]}>
              {STATUS_LABELS[campaign.status]}
            </Badge>
            <span className={styles.period}>
              {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
            </span>
            <Button
              variant="outline"
              size="sm"
              leftIcon={linkCopied ? <Check size={14} /> : <Link2 size={14} />}
              onClick={handleCopyWidgetLink}
            >
              {linkCopied ? 'Link copiado!' : 'Copiar link da campanha'}
            </Button>
          </div>
          {campaign.description && (
            <p className={styles.description}>{campaign.description}</p>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div
        className={styles.tabList}
        role="tablist"
        aria-label="Seções da campanha"
      >
        <button
          type="button"
          role="tab"
          id="tab-packages"
          aria-selected={activeTab === 'packages'}
          aria-controls="panel-packages"
          className={[
            styles.tab,
            activeTab === 'packages' ? styles.tabActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('packages')}
        >
          Pacotes
        </button>
        <button
          type="button"
          role="tab"
          id="tab-enrollments"
          aria-selected={activeTab === 'enrollments'}
          aria-controls="panel-enrollments"
          className={[
            styles.tab,
            activeTab === 'enrollments' ? styles.tabActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('enrollments')}
        >
          Inscrições
        </button>
      </div>

      {/* Packages tab */}
      {activeTab === 'packages' && (
        <div
          role="tabpanel"
          id="panel-packages"
          aria-labelledby="tab-packages"
          className={styles.tabPanel}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Pacotes da Campanha</h2>
            <Button
              size="sm"
              leftIcon={<Plus size={16} />}
              onClick={openCreatePackageModal}
              disabled={campaign.status === 'FINISHED'}
            >
              Novo Pacote
            </Button>
          </div>

          {campaign.packages.length === 0 ? (
            <p className={styles.emptyMessage}>
              Nenhum pacote cadastrado. Crie o primeiro pacote.
            </p>
          ) : (
            <div className={styles.packageList}>
              {campaign.packages.map((pkg) => {
                const sortedItems = [...pkg.items].sort(
                  (a, b) => a.orderIndex - b.orderIndex,
                );

                return (
                  <article
                    key={pkg.id}
                    className={styles.packageCard}
                    aria-label={`Pacote: ${pkg.name}`}
                  >
                    <div className={styles.packageCardHeader}>
                      <div className={styles.packageCardHeaderTop}>
                        <div>
                          <h3 className={styles.packageName}>{pkg.name}</h3>
                          {pkg.description && (
                            <p className={styles.packageDescription}>
                              {pkg.description}
                            </p>
                          )}
                        </div>
                        <span className={styles.packagePrice}>
                          {formatCurrency(pkg.price)}
                        </span>
                      </div>
                      <div className={styles.packageActions}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditPackageModal(pkg)}
                          disabled={campaign.status === 'FINISHED'}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePackageMutation.mutate(pkg.id)}
                          disabled={
                            deletePackageMutation.isPending ||
                            campaign.status === 'FINISHED'
                          }
                        >
                          Excluir
                        </Button>
                      </div>
                    </div>

                    <div className={styles.packageCardBody}>
                      <div className={styles.packageItemsHeader}>
                        <span className={styles.packageItemsTitle}>
                          Itens do Pacote
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Plus size={14} />}
                          onClick={() => openAddItemModal(pkg.id)}
                          disabled={campaign.status === 'FINISHED'}
                        >
                          Adicionar Item
                        </Button>
                      </div>

                      {sortedItems.length === 0 ? (
                        <p className={styles.emptyItems}>
                          Nenhum item adicionado.
                        </p>
                      ) : (
                        <ol className={styles.itemList} role="list">
                          {sortedItems.map((item, index) => (
                            <li key={item.id} className={styles.item}>
                              <span className={styles.itemOrder}>
                                {index + 1}
                              </span>
                              <span className={styles.itemName}>
                                {item.procedureTypeName}
                              </span>
                              <span className={styles.itemQuantity}>
                                {item.quantity}x
                              </span>
                              <div className={styles.itemActions}>
                                <button
                                  type="button"
                                  className={styles.reorderButton}
                                  onClick={() =>
                                    handleMoveItem(pkg, item, 'up')
                                  }
                                  disabled={
                                    index === 0 ||
                                    reorderItemsMutation.isPending ||
                                    campaign.status === 'FINISHED'
                                  }
                                  aria-label={`Mover ${item.procedureTypeName} para cima`}
                                >
                                  <ChevronUp size={12} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className={styles.reorderButton}
                                  onClick={() =>
                                    handleMoveItem(pkg, item, 'down')
                                  }
                                  disabled={
                                    index === sortedItems.length - 1 ||
                                    reorderItemsMutation.isPending ||
                                    campaign.status === 'FINISHED'
                                  }
                                  aria-label={`Mover ${item.procedureTypeName} para baixo`}
                                >
                                  <ChevronDown size={12} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className={styles.removeItemButton}
                                  onClick={() =>
                                    removeItemMutation.mutate({
                                      packageId: pkg.id,
                                      itemId: item.id,
                                    })
                                  }
                                  disabled={
                                    removeItemMutation.isPending ||
                                    campaign.status === 'FINISHED'
                                  }
                                  aria-label={`Remover ${item.procedureTypeName}`}
                                >
                                  <Trash2 size={12} aria-hidden="true" />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}

                      {/* Timeline preview */}
                      {sortedItems.length > 0 && (
                        <div className={styles.timelinePreview}>
                          <h4 className={styles.timelinePreviewTitle}>
                            Visualização da Jornada
                          </h4>
                          <JourneyTimeline
                            packageItems={sortedItems}
                            ariaLabel={`Linha do tempo do pacote ${pkg.name}`}
                          />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Enrollments tab */}
      {activeTab === 'enrollments' && (
        <div
          role="tabpanel"
          id="panel-enrollments"
          aria-labelledby="tab-enrollments"
          className={styles.tabPanel}
        >
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Inscrições</h2>
          </div>

          {isJourneysLoading ? (
            <div className={styles.loadingState} role="status" aria-busy="true">
              <p>Carregando inscrições...</p>
            </div>
          ) : (
            <Table
              columns={enrollmentColumns}
              data={journeys}
              keyExtractor={(journey) => journey.id}
              emptyMessage="Nenhum paciente inscrito nesta campanha."
            />
          )}
        </div>
      )}

      {/* Create/Edit package modal */}
      <Modal
        isOpen={isPackageModalOpen}
        onClose={closePackageModal}
        title={editingPackage ? 'Editar Pacote' : 'Novo Pacote'}
        size="md"
      >
        <form onSubmit={handlePackageSubmit} className={styles.form} noValidate>
          <Input
            label="Nome"
            value={pkgName}
            onChange={(e) => setPkgName(e.target.value)}
            placeholder="Nome do pacote"
            required
            fullWidth
          />
          <div className={styles.textareaWrapper}>
            <label htmlFor="package-description" className={styles.textareaLabel}>
              Descrição (opcional)
            </label>
            <textarea
              id="package-description"
              className={styles.textarea}
              value={pkgDescription}
              onChange={(e) => setPkgDescription(e.target.value)}
              rows={3}
              placeholder="Descreva o pacote..."
            />
          </div>
          <Input
            label="Preco (R$)"
            type="number"
            value={pkgPrice}
            onChange={(e) => setPkgPrice(e.target.value)}
            placeholder="0.00"
            min={0}
            step={0.01}
            required
            fullWidth
          />
          {pkgError && (
            <p className={styles.formError} role="alert">
              {pkgError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closePackageModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={
                createPackageMutation.isPending || updatePackageMutation.isPending
              }
            >
              {editingPackage ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add item to package modal */}
      <Modal
        isOpen={addItemToPackageId !== null}
        onClose={closeAddItemModal}
        title="Adicionar Item ao Pacote"
        size="sm"
      >
        <form onSubmit={handleAddItem} className={styles.form} noValidate>
          {procedureTypeOptions.length === 0 ? (
            <p className={styles.emptyMessage}>
              Nenhum procedimento ativo disponível.
            </p>
          ) : (
            <Select
              label="Procedimento"
              options={procedureTypeOptions}
              value={itemProcedureTypeId}
              onChange={(e) => setItemProcedureTypeId(e.target.value)}
              placeholder="Selecione um procedimento"
              fullWidth
            />
          )}
          <Input
            label="Quantidade"
            type="number"
            value={itemQuantity}
            onChange={(e) => setItemQuantity(e.target.value)}
            min={1}
            required
            fullWidth
          />
          <div className={styles.textareaWrapper}>
            <label htmlFor="item-notes" className={styles.textareaLabel}>
              Observações (opcional)
            </label>
            <textarea
              id="item-notes"
              className={styles.textarea}
              value={itemNotes}
              onChange={(e) => setItemNotes(e.target.value)}
              rows={2}
              placeholder="Observações sobre este item..."
            />
          </div>
          {itemError && (
            <p className={styles.formError} role="alert">
              {itemError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeAddItemModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={addItemMutation.isPending}
              disabled={procedureTypeOptions.length === 0}
            >
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
